/*
 * region-codes/index.js — страница /region-codes: поле «код или номер», ответы по
 * системам, таблица всех субъектов, «мой регион». Разбор ввода — engine.js (без DOM,
 * его гоняет node --test); регион человека — общий примитив ../regions/index.js.
 * Почему так — docs/region-codes.md.
 *
 * Файл мобильных диапазонов (≈ 220 КБ) грузится, только когда ищут мобильный номер.
 * Весь текст для человека — из перевода (window.__REGCODES.T).
 */
import { indexCodes, detect, decodeMobile, findMobile, mobilePrefix, plateAnswer } from './engine.js';
import { whenProfileCar, plateRegionHint } from '../car-catalog/profile-car.js';
import {
    createRegions, myRegion, rememberPlace, rememberRegion, lastPlace, lastRegion, profilePlace,
} from '../regions/index.js';

const BOOT = window.__REGCODES || { T: {}, cfg: {} };
const T = BOOT.T || {};
const cfg = BOOT.cfg || {};
const SLUG = cfg.slug || 'region-codes';
const $ = (id) => document.getElementById(id);

/* ── мелочи ───────────────────────────────────────────────────────────────── */

function tr(key, params) {
    let s = T[key] != null ? String(T[key]) : key;
    if (params) {
        for (const k of Object.keys(params).sort((a, b) => b.length - a.length)) s = s.split(':' + k).join(String(params[k]));
    }
    return s;
}
const stage = (n) => { try { if (window.__stage) window.__stage(n); } catch (e) { /* телеметрия не ломает страницу */ } };
const loadSetting = (k, d) => { try { return window.__toolSettings ? window.__toolSettings.load(SLUG, k, d) : d; } catch (e) { return d; } };
const saveSetting = (k, v) => { try { if (window.__toolSettings) window.__toolSettings.save(SLUG, k, v); } catch (e) { /* приватный режим */ } };
const toast = (msg, type) => { try { if (window.__toast) window.__toast(msg, type || 'info'); } catch (e) { /* нет тостов */ } };
const intlLocale = () => { try { return window.__intlLocale ? window.__intlLocale() : 'ru'; } catch (e) { return 'ru'; } };
const norm = (s) => String(s || '').toLowerCase().replace(/ё/g, 'е').trim();

function node(tag, cls, text) {
    const e = document.createElement(tag);
    if (cls) e.className = cls;
    if (text != null) e.textContent = text;
    return e;
}

let ix = null;          // индекс codes.json
let mobile = null;      // индекс phone-mobile.json, когда загружен
let mobileP = null;     // промис загрузки
let RG = null;          // примитив регионов
let mineCode = '';
let lastSent = '';
let settleTimer = 0;

/* ── названия ─────────────────────────────────────────────────────────────── */

function nameOf(code) {
    if (code === 'RU') return tr('ph_whole');
    const r = ix && ix.by.get(code);
    return r ? r.n : code;
}
const namesOf = (codes) => (codes || []).map(nameOf).join(', ');

function pct(share) {
    const v = share * 100;
    if (v > 0 && v < 1) return '<1';
    try { return new Intl.NumberFormat(intlLocale(), { maximumFractionDigits: 0 }).format(v); } catch (e) { return String(Math.round(v)); }
}
function fmtPhone(n10) {
    const d = String(n10);
    if (d.length !== 10) return '+7 ' + d;
    return `+7 ${d.slice(0, 3)} ${d.slice(3, 6)}-${d.slice(6, 8)}-${d.slice(8)}`;
}
function badge() {
    const b = node('span', 'rc-badge', tr('unverified'));
    b.title = tr('unverified_t');
    return b;
}

/* ── карточки ответов ─────────────────────────────────────────────────────── */

function card(sysLabel, lead, maybe) {
    const c = node('div', 'rc-card' + (lead ? ' lead' : '') + (maybe ? ' weak' : ''));
    const h = node('div', 'rc-sys', sysLabel);
    if (maybe) h.appendChild(node('i', '', '· ' + tr('maybe', { what: maybe })));
    c.appendChild(h);
    return c;
}
function main(c, text, muted) {
    const m = node('div', 'rc-main' + (muted ? ' muted' : ''), text);
    c.appendChild(m);
    return m;
}
const sub = (c, text) => c.appendChild(node('p', 'rc-sub', text));
const note = (c, text) => c.appendChild(node('p', 'rc-note', text));

function platesCard(a, lead) {
    const c = card(tr('sys_plates'), lead);
    const pl = a.plate;
    if (a.bad === 'zero3') { main(c, tr('pl_zero3'), true); return c; }
    if (a.bad === 'none') { main(c, tr('pl_none', { code: a.code }), true); return c; }
    const official = a.entries.filter((e) => e.s === 'o');
    const legacy = a.entries.filter((e) => e.s === 'l');
    if (official.length) {
        const m = main(c, official.map((e) => nameOf(e.r)).join(' · '));
        const since = official.find((e) => e.since);
        if (since) m.appendChild(node('span', 'rc-when', tr('pl_since', { date: since.since })));
    } else if (legacy.length) {
        const m = main(c, legacy.map((e) => nameOf(e.r)).join(' · '));
        if (legacy.some((e) => e.u)) m.appendChild(badge());
        sub(c, tr('pl_old_only', { code: a.via }));
        for (const e of legacy) if (!e.was) note(c, tr('pl_old_same', { until: e.until }));
    }
    if (pl) sub(c, tr('pl_plate', { series: pl.series, code: a.code }));
    sub(c, a.three ? tr('pl_three', { via: a.via }) : tr('pl_code', { code: a.code }));
    if (official.length) {
        for (const e of legacy) {
            const box = node('p', 'rc-old', tr('pl_old', { code: a.via, was: e.was || nameOf(e.r), until: e.until, region: nameOf(e.r) }));
            if (e.u) box.appendChild(badge());
            c.appendChild(box);
        }
        const r = ix.by.get(official[0].r);
        if (r && r.pl && official.length === 1) {
            const all = [...r.pl, ...(r.pl3 || [])];
            if (all.length > 1) note(c, tr('pl_all', { list: all.join(', ') }));
        }
        if (r && r.note) note(c, r.note);
    }
    if (pl && pl.latin) note(c, tr('pl_latin'));
    if (pl && pl.badLetters) note(c, tr('pl_bad', { letters: pl.badLetters }));
    if (pl && pl.type === 'moto') note(c, tr('pl_moto'));
    if (pl && pl.type === 'other') note(c, tr('pl_other'));
    return c;
}

const TAX_LABEL = { tax: 'sys_tax', inn: 'sys_inn', kpp: 'sys_kpp', ogrn: 'sys_ogrn', ifns: 'sys_ifns' };

function taxCard(a, lead, maybe) {
    const c = card(tr(TAX_LABEL[a.sys]), lead, maybe);
    if (a.sys === 'inn' || a.sys === 'ogrn') {
        const head = node('p', 'rc-sub');
        const kind = a.sys === 'inn' ? (a.person ? tr('inn_fl') : tr('inn_ul')) : tr('ogrn_' + a.kind);
        head.appendChild(document.createTextNode(kind + ' · '));
        const ok = a.sys === 'inn' ? (a.valid ? 'inn_ok' : 'inn_bad') : (a.valid ? 'ogrn_ok' : 'ogrn_bad');
        head.appendChild(node('span', a.valid ? 'rc-ok' : 'rc-bad', tr(ok)));
        c.appendChild(head);
    }
    if (a.largest) {
        main(c, tr('kpp_largest', { office: a.office }), true);
        return c;
    }
    if (a.regions.length) {
        const m = main(c, namesOf(a.regions));
        if (a.code === '83') m.appendChild(badge());
    } else if (a.former) {
        main(c, tr('tax_gone', { code: a.code }), true);
        const p = sub(c, tr('tax_former', { was: a.former[1], region: nameOf(a.former[0]) }));
        p.appendChild(badge());
    } else {
        main(c, tr('tax_none', { code: a.code }), true);
    }
    const r = a.regions.length ? ix.by.get(a.regions[0]) : null;
    if (a.sys === 'tax' && r) sub(c, tr('tax_code', { code: a.code, n: r.taxN != null ? r.taxN : '—' }));
    if (a.sys === 'inn' && a.regions.length) sub(c, tr('inn_region', { office: a.office, name: namesOf(a.regions) }));
    if (a.sys === 'kpp' && a.regions.length) sub(c, tr('kpp_region', { office: a.office, name: namesOf(a.regions) }));
    if (a.sys === 'ifns') sub(c, tr('ifns_line', { office: a.office, code: a.code }));
    if (a.sys === 'ogrn') sub(c, tr('ogrn_line', { year: a.year, code: a.code }));
    if (a.code === '83') note(c, tr('tax_nen'));
    if (a.code === '99') note(c, tr('tax_99'));
    if (a.code === '96') note(c, tr('tax_96'));
    if (a.sys === 'inn') note(c, tr('inn_note'));
    if (a.sys === 'kpp') note(c, tr('kpp_note'));
    if (a.sys === 'kpp' && a.bik) note(c, tr('kpp_bik'));
    if (a.sys === 'ogrn') note(c, tr('ogrn_note'));
    return c;
}

function cadCard(a, lead) {
    const c = card(tr('sys_cad'), lead);
    if (!a.regions.length) { main(c, tr('cad_none', { code: a.code }), true); return c; }
    main(c, namesOf(a.regions));
    const r = ix.by.get(a.regions[0]);
    const d = r && (r.cad || []).find((x) => x[0] === a.code);
    sub(c, tr('cad_line', { district: d ? d[1] : a.code, code: a.code }));
    if (a.parts) {
        let s = tr('cad_parts', { okrug: a.parts.okrug, district: a.parts.district });
        if (a.parts.quarter) s += tr('cad_quarter', { quarter: a.parts.quarter });
        if (a.parts.parcel) s += tr('cad_parcel', { parcel: a.parts.parcel });
        note(c, s + '.');
    }
    if (r && r.cad && r.cad.length > 1) note(c, tr('cad_multi', { list: r.cad.map((x) => `${x[0]} «${x[1]}»`).join(', ') }));
    return c;
}

function okatoCard(a, lead, maybe) {
    const isOkato = a.sys === 'okato';
    const c = card(tr(isOkato ? 'sys_okato' : 'sys_oktmo'), lead, maybe);
    if (!a.regions.length) { main(c, tr(isOkato ? 'okato_none' : 'oktmo_none', { key: a.key }), true); return c; }
    main(c, namesOf(a.regions));
    sub(c, tr(isOkato ? 'okato_line' : 'oktmo_line', { key: a.key, ed: isOkato ? cfg.okatoEd : cfg.oktmoEd }));
    if (a.aoInside) note(c, tr('okato_ao'));
    return c;
}

function phoneCard(a, lead, maybe) {
    const c = card(tr('sys_phone') + (a.n10 && !a.codeOnly ? ' · ' + (a.partial ? '+7 ' + a.n10 : fmtPhone(a.n10)) : ''), lead, maybe);
    switch (a.kind) {
        case 'geo': {
            if (!a.regions.length) { main(c, tr('ph_none', { abc: a.abc }), true); break; }
            main(c, nameOf(a.regions[0].code));
            sub(c, tr('ph_geo', { abc: a.abc, list: a.regions.map((x) => nameOf(x.code)).join(', ') }));
            if (a.regions.length > 1) {
                const ul = node('ul', 'rc-list');
                for (const x of a.regions) ul.appendChild(node('li', '', tr('ph_share', { name: nameOf(x.code), pct: pct(x.share) })));
                c.appendChild(ul);
            }
            break;
        }
        case 'mobile':
            mobileInto(c, a);
            break;
        case 'nongeo': main(c, tr('ph_nongeo', { abc: a.abc }), true); break;
        case 'kz': main(c, tr('ph_kz', { abc: a.abc }), true); break;
        case 'short': main(c, tr('ph_short'), true); break;
        default: main(c, tr('ph_none', { abc: a.abc }), true);
    }
    return c;
}

function opName(op) { return op ? (op[2] || op[0]) : ''; }
function opFull(op) { return op ? (op[2] ? `${op[2]} (${op[0]})` : op[0]) : ''; }

/* Ответ про мобильный номер ждёт реестр: карточка встаёт сразу, данные — по загрузке. */
function mobileInto(c, a) {
    const body = node('div', '');
    c.appendChild(body);
    const fill = () => {
        body.textContent = '';
        if (a.codeOnly || a.partial) {
            const rows = mobilePrefix(mobile, a.n10);
            if (!rows.length) { main(body, tr('ph_mobile_none'), true); return; }
            main(body, opName(rows[0].op));
            sub(body, tr('ph_prefix', { prefix: a.n10 }));
            const ul = node('ul', 'rc-list');
            for (const r of rows.slice(0, 8)) ul.appendChild(node('li', '', tr('ph_prefix_row', { op: opName(r.op), regions: namesOf(r.groups), pct: pct(r.share) })));
            body.appendChild(ul);
        } else {
            const hit = findMobile(mobile, a.n10);
            if (!hit) { main(body, tr('ph_mobile_none'), true); return; }
            main(body, opName(hit.op));
            sub(body, tr('ph_mobile', { op: opFull(hit.op), regions: namesOf(hit.groups) }));
            note(body, tr('ph_range', { from: fmtPhone(String(hit.from)), to: fmtPhone(String(hit.to)) }));
        }
        body.appendChild(node('p', 'rc-port', tr('ph_port')));
    };
    if (mobile) { fill(); return; }
    const load = () => {
        body.textContent = '';
        main(body, tr('ph_mobile_wait'), true);
        ensureMobile().then(() => { if (body.isConnected) fill(); }, () => {
            if (!body.isConnected) return;
            body.textContent = '';
            main(body, tr('mobile_error'), true);
            const b = node('button', 'tc-btn ghost sm', tr('retry'));
            b.type = 'button';
            b.addEventListener('click', load);
            body.appendChild(b);
        });
    };
    if (a.codeOnly) {
        // Голые три цифры — прежде всего код с номера машины; реестр — по кнопке.
        const b = node('button', 'tc-btn ghost sm', tr('ph_prefix_btn', { abc: a.abc }));
        b.type = 'button';
        b.addEventListener('click', load);
        body.appendChild(b);
        return;
    }
    load();
}

function ensureMobile() {
    if (mobile) return Promise.resolve(mobile);
    if (!mobileP) {
        stage('mobile_load');
        mobileP = fetch(cfg.mobileUrl, { credentials: 'omit' })
            .then((r) => { if (!r.ok) throw Object.assign(new Error('phone-mobile HTTP ' + r.status), { status: r.status }); return r.json(); })
            .then((j) => { mobile = decodeMobile(j); return mobile; })
            .catch((e) => {
                mobileP = null;
                try { if (window.__handleError) window.__handleError(e, 'region_codes_mobile', { say: tr('mobile_error') }); } catch (x) { /* нет обработчика */ }
                throw e;
            });
    }
    return mobileP;
}

function namesCards(a, out) {
    for (const code of a.regions) {
        const r = ix.by.get(code);
        if (!r) continue;
        const c = card(tr('sys_names'), out.childElementCount === 0);
        main(c, r.n);
        c.appendChild(summaryList(r));
        if (r.note) note(c, r.note);
        out.appendChild(c);
    }
}

/** Все коды одного региона — список «система: коды». */
function summaryList(r) {
    const dl = node('dl', 'rc-dl');
    const row = (k, v) => { if (!v) return; dl.appendChild(node('dt', '', tr(k))); dl.appendChild(node('dd', '', v)); };
    const pl = [...(r.pl || []), ...(r.pl3 || [])].join(', ');
    const old = (r.old || []).map((o) => `${tr('earlier')} ${o.c}${o.u ? '†' : ''}`).join(', ');
    row('th_plates', [pl, old].filter(Boolean).join('; '));
    row('th_tax', r.tax ? r.tax + (r.taxAlso && r.taxAlso.length ? ` (${tr('tax_nen_cell')}†)` : '') : '');
    row('th_cad', (r.cad || []).map((x) => x[0]).join(', '));
    row('th_okato', r.okato || '');
    row('th_oktmo', r.oktmo || '');
    row('th_abc', (r.abc || []).join(', '));
    return dl;
}

const WHAT = { okato: 'what_okato', oktmo: 'what_oktmo', inn: 'what_inn', phone: 'what_phone' };

function render(res) {
    const out = $('rcOut');
    out.textContent = '';
    const bare = res.kind === 'bare2' || res.kind === 'bare3';
    res.answers.forEach((a, i) => {
        const lead = i === 0;
        const maybe = !bare && i > 0 && WHAT[a.sys] ? tr(WHAT[a.sys]) : '';
        let c = null;
        switch (a.sys) {
            case 'plates': c = platesCard(a, lead); break;
            case 'tax': case 'inn': case 'kpp': case 'ogrn': case 'ifns': c = taxCard(a, lead, maybe); break;
            case 'cad': c = cadCard(a, lead); break;
            case 'okato': case 'oktmo': c = okatoCard(a, lead, maybe); break;
            case 'phone': c = phoneCard(a, lead, maybe); break;
            case 'passport': c = card(tr('sys_passport'), lead); main(c, tr('passport_main'), true); sub(c, tr('passport_sub')); break;
            case 'names': namesCards(a, out); break;
            default: break;
        }
        if (c) out.appendChild(c);
    });
    if (!res.answers.length && res.kind !== 'empty') {
        const c = card('', true);
        c.firstChild.remove();
        const msg = res.kind === 'plate-partial' ? tr('unk_partial') : res.digits === 6 ? tr('unk_6') : res.digits ? tr('unk_digits', { n: res.digits }) : tr('unk');
        main(c, msg, true);
        out.appendChild(c);
    }
    $('rcShareRow').hidden = !res.answers.length;
    $('rcClear').hidden = !res.q;
    markHits(res);
}

/** Строки таблицы, о которых ответ (главный ответ — первый). */
function markHits(res) {
    const want = new Set();
    const a = res.answers[0];
    if (a) {
        if (a.sys === 'plates') a.entries.filter((e) => e.s === 'o').forEach((e) => want.add(e.r));
        else if (a.sys === 'names') a.regions.forEach((c) => want.add(c));
        else if (a.sys === 'phone') (a.regions || []).forEach((x) => want.add(x.code));
        else (a.regions || []).forEach((c) => want.add(c));
    }
    for (const tr_ of document.querySelectorAll('#rcTable tbody tr[data-code]')) tr_.classList.toggle('rc-hit', want.has(tr_.dataset.code));
}

/* ── ввод ─────────────────────────────────────────────────────────────────── */

function update(src) {
    if (!ix) return;
    const q = $('rcQ').value;
    const res = detect(q, ix);
    render(res);
    clearTimeout(settleTimer);
    if (!res.answers.length) { syncUrl(''); return; }
    // Ответ «устоялся», когда человек перестал печатать: тогда он и считается
    // результатом и попадает в ссылку (?q=). Пример и ссылка — сразу.
    const settle = () => {
        syncUrl(res.q);
        if (res.q === lastSent) return;
        lastSent = res.q;
        stage('answer');
        try {
            if (window.__aeToolComplete) window.__aeToolComplete({ kind: res.kind, sys: res.answers[0].sys, n: res.answers.length, src: src || 'typed' });
        } catch (e) { /* телеметрия не ломает страницу */ }
    };
    if (src === 'typed') settleTimer = setTimeout(settle, 900); else settle();
}

function syncUrl(q) {
    try {
        const u = new URL(location.href);
        if (q) u.searchParams.set('q', q); else u.searchParams.delete('q');
        if (u.href !== location.href) history.replaceState(history.state, '', u.href);
    } catch (e) { /* без history — без ссылки */ }
}

function pick(q) {
    $('rcQ').value = q;
    update('example');
}

async function share() {
    const href = location.href;
    try {
        await navigator.clipboard.writeText(href);
        toast(tr('share_ok'), 'success');
    } catch (e) {
        toast(tr('share_fail'), 'info');
    }
}

/* ── таблица: фильтр и сортировка ─────────────────────────────────────────── */

function filterRows() {
    const words = norm($('rcFilter').value).split(/\s+/).filter(Boolean);
    const rows = document.querySelectorAll('#rcTable tbody tr[data-code]');
    let shown = 0;
    for (const r of rows) {
        const k = r.dataset.k || '';
        const ok = !words.length || words.every((w) => k.includes(w));
        r.hidden = !ok;
        if (ok) shown++;
    }
    const other = $('rcOther');
    if (other) other.hidden = ![...other.querySelectorAll('tr[data-code]')].some((r) => !r.hidden);
    $('rcCount').textContent = shown ? tr('filter_count', { n: shown, total: rows.length }) : tr('filter_none');
}

let collator = null;
function sortBy(col, dir) {
    const body = $('rcRows');
    if (!collator) { try { collator = new Intl.Collator(intlLocale()); } catch (e) { collator = { compare: (a, b) => a.localeCompare(b) }; } }
    const rows = [...body.querySelectorAll('tr[data-code]')];
    const key = 's' + col[0].toUpperCase() + col.slice(1);
    rows.sort((a, b) => {
        let d;
        if (col === 'name') d = collator.compare(a.dataset.sName, b.dataset.sName);
        else d = (+a.dataset[key]) - (+b.dataset[key]) || collator.compare(a.dataset.sName, b.dataset.sName);
        return dir === 'desc' ? -d : d;
    });
    for (const r of rows) body.appendChild(r);
    for (const th of document.querySelectorAll('#rcTable thead th')) {
        const b = th.querySelector('button');
        if (b && b.dataset.sort === col) th.setAttribute('aria-sort', dir === 'desc' ? 'descending' : 'ascending');
        else th.removeAttribute('aria-sort');
    }
}

function wireTable() {
    $('rcFilter').addEventListener('input', filterRows);
    for (const b of document.querySelectorAll('#rcTable thead button[data-sort]')) {
        b.addEventListener('click', () => {
            const th = b.closest('th');
            const dir = th.getAttribute('aria-sort') === 'ascending' ? 'desc' : 'asc';
            sortBy(b.dataset.sort, dir);
            saveSetting('sort', b.dataset.sort + ':' + dir);
        });
    }
    const saved = String(loadSetting('sort', '') || '');
    const m = saved.match(/^(name|plates|tax|cad|okato|oktmo|abc):(asc|desc)$/);
    if (m && saved !== 'plates:asc') sortBy(m[1], m[2]);
}

/* ── мой регион ───────────────────────────────────────────────────────────── */

function setMine(code, why) {
    mineCode = code || '';
    for (const r of document.querySelectorAll('#rcTable tbody tr[data-code]')) r.classList.toggle('rc-mine-row', r.dataset.code === mineCode);
    const v = $('rcMineV');
    v.textContent = '';
    if (!mineCode) { v.textContent = tr('mine_none'); return; }
    const r = ix && ix.by.get(mineCode);
    v.appendChild(document.createTextNode(r ? r.n : mineCode));
    if (r) {
        const small = node('small', '', (why ? why + ' · ' : '') + tr('mine_codes', {
            plates: (r.pl || []).join(', ') || '—', tax: r.tax || '—', cad: (r.cad || []).map((x) => x[0]).join(', ') || '—', okato: r.okato || '—',
        }));
        v.appendChild(small);
    }
    const sel = $('rcMineSel');
    if (sel && [...sel.options].some((o) => o.value === mineCode)) sel.value = mineCode;
}

function regionsPrimitive() {
    if (!RG) RG = createRegions(Object.assign({ locale: 'ru' }, cfg.regions || {}));
    return RG;
}

async function initMine() {
    // Контуры (≈ 320 КБ) грузятся, только если человеку есть что вспомнить: выбор на
    // сайте или город профиля. Без них «мой регион» просто не выбран — без вопроса
    // о геолокации.
    if (!lastRegion() && !lastPlace() && !profilePlace()) return;
    try {
        const mine = await myRegion(regionsPrimitive(), 'RUS');
        if (mine.last) setMine(mine.last.code, tr('mine_last'));
        else if (mine.profile) setMine(mine.profile.code, tr('mine_profile'));
    } catch (e) { /* без памяти — человек выберет сам */ }
}

function locate() {
    const msg = $('rcMineMsg');
    if (!navigator.geolocation) { msg.textContent = tr('mine_nogeo'); return; }
    msg.textContent = tr('mine_locating');
    navigator.geolocation.getCurrentPosition(async (pos) => {
        const lat = pos.coords.latitude, lon = pos.coords.longitude;
        try {
            const w = await regionsPrimitive().at(lat, lon, { a3: 'RUS' });
            if (w.region) {
                setMine(w.region.code, w.region.exact ? tr('mine_located') : tr('mine_near'));
                rememberPlace({ lat, lon });
                msg.textContent = '';
                stage('mine_located');
            } else if (w.country && w.country.name) {
                msg.textContent = tr('mine_abroad', { country: w.country.name });
            } else {
                msg.textContent = tr('mine_fail');
            }
        } catch (e) {
            msg.textContent = tr('mine_fail');
            try { if (window.__handleError) window.__handleError(e, 'region_codes_locate', { say: tr('mine_fail') }); } catch (x) { /* нет обработчика */ }
        }
    }, () => { msg.textContent = tr('mine_denied'); }, { timeout: 15000, maximumAge: 600000 });
}

function wireMine() {
    $('rcMineSel').addEventListener('change', (e) => {
        const code = e.target.value;
        if (!code) return;
        const r = ix && ix.by.get(code);
        rememberRegion({ code, a3: 'RUS', name: r ? r.n : '' });
        setMine(code, tr('mine_chosen'));
        $('rcMineMsg').textContent = '';
    });
    $('rcLocate').addEventListener('click', locate);
}

/* ── регион номера из профиля ─────────────────────────────────────────────
   Вошедшему владельцу с российским номером в профиле (/api/session-bootstrap)
   — тихая строка «Ваш регион по номеру из профиля: 150 — Московская область».
   В поле номер не пишется: поле уходит в адрес страницы. «Показать» ставит
   в поле только код; «Скрыть» запоминает этот код и больше его не предлагает. */
function offerProfileRegion() {
    const box = $('rcProf');
    if (!box) return;
    let code = '';
    const hide = () => { box.hidden = true; };
    whenProfileCar((b) => {
        const h = plateRegionHint({ car: b && b.loggedIn ? b.car : null, current: $('rcQ').value, fromUrl: false });
        if (!h || loadSetting('prof_hint_off', '') === h.code) return;
        const a = plateAnswer(h.code, ix);
        const names = (a.entries || []).filter((e) => e.s === 'o').map((e) => nameOf(e.r));
        if (!names.length) return;
        code = h.code;
        $('rcProfText').textContent = tr('prof_region', { code, name: names.join(' · ') });
        box.hidden = false;
        stage('profile_region');
    });
    $('rcProfShow').addEventListener('click', () => { if (code) { hide(); $('rcQ').value = code; update('profile'); } });
    $('rcProfHide').addEventListener('click', () => { if (code) saveSetting('prof_hint_off', code); hide(); });
    $('rcQ').addEventListener('input', () => { if ($('rcQ').value.trim()) hide(); });
}

/* ── запуск ───────────────────────────────────────────────────────────────── */

function wireInput() {
    const q = $('rcQ');
    q.addEventListener('input', () => update('typed'));
    q.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') { e.preventDefault(); clearTimeout(settleTimer); update('enter'); }
    });
    $('rcClear').addEventListener('click', () => { q.value = ''; update('typed'); q.focus(); });
    for (const b of document.querySelectorAll('.rc-ex [data-q]')) b.addEventListener('click', () => pick(b.dataset.q));
    $('rcShare').addEventListener('click', share);
    document.addEventListener('keydown', (e) => {
        if (e.key !== '/' || e.ctrlKey || e.metaKey || e.altKey) return;
        if (window.__keyOwnedByFocus ? window.__keyOwnedByFocus(e) : /^(INPUT|TEXTAREA|SELECT)$/.test((e.target && e.target.tagName) || '')) return;
        e.preventDefault();
        q.focus();
    });
}

async function boot() {
    let json;
    try {
        const r = await fetch(cfg.codesUrl, { credentials: 'omit' });
        if (!r.ok) throw Object.assign(new Error('codes HTTP ' + r.status), { status: r.status });
        json = await r.json();
    } catch (e) {
        const el = $('rcLoadError');
        el.textContent = tr('load_error');
        el.hidden = false;
        try { if (window.__handleError) window.__handleError(e, 'region_codes_load', { say: tr('load_error') }); } catch (x) { /* нет обработчика */ }
        if (window.__earlyAction) window.__earlyAction.claim(SLUG);
        return;
    }
    ix = indexCodes(json);
    wireInput();
    wireTable();
    wireMine();
    let src = 'typed';
    let opened = false;
    try {
        const fromUrl = new URL(location.href).searchParams.get('q');
        opened = !!fromUrl;
        if (fromUrl && !$('rcQ').value) { $('rcQ').value = fromUrl.slice(0, 60); src = 'url'; }
    } catch (e) { /* без URL API */ }
    // Набранное до загрузки скрипта не пропадает: отвечаем на то, что уже в поле.
    if ($('rcQ').value) update(src === 'url' ? 'url' : 'enter');
    if (!opened) offerProfileRegion();
    if (window.__earlyAction) {
        window.__earlyAction.claim(SLUG, (a, el) => {
            if (a === 'ex' && el && el.dataset.q) pick(el.dataset.q);
            else if (a === 'locate') locate();
        });
    }
    stage('ready');
    initMine();
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
